import { Button, Container, Group, Stack, Switch, Text, Title } from '@mantine/core';
import { IconLayoutColumns, IconPlus } from '@tabler/icons-react';
import { LocalhostOnlyBadge } from '@/components/LocalhostOnlyBadge';
import { isUnifyAdminShellRole } from '@/config/featureVisibility';
import { usePermissions } from '@/contexts/PermissionContext';
import { useDisclosure } from '@mantine/hooks';
import { useMedplum, useSearchResources } from '@medplum/react-hooks';
import type { Organization } from '@medplum/fhirtypes';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ORG_TYPE_PAYER } from '@unify/fhir-model/constants';
import {
  buildPayerOrganization,
  getPayerState,
  getSoharPayerId,
  getStediPayerNetworkId,
  getVerifiableCommercialPayerId,
  getVerifiableMedicaidPayerId,
  getVerifiableMedicarePayerId,
  setPayerState,
  setSoharPayerId,
  setStediPayerNetworkId,
  setVerifiableCommercialPayerId,
  setVerifiableMedicaidPayerId,
  setVerifiableMedicarePayerId,
} from '@unify/fhir-model/resources/organization';
import { UnifyTable, type UnifyTableColumn } from '@/components/UnifyTable';
import { ColumnSelectModal } from '@/pages/episode-tracker/components/ColumnSelectModal';
import type { PayerFormInput } from '@/schemas/payer.schema';
import { AddPayerModal } from './AddPayerModal';

type InvalidateSearches = (resourceType: 'Organization') => void;

interface PayerRow {
  id: string;
  name: string;
  active: boolean;
  state?: string;
  soharId?: string;
  stediNetworkId?: string;
  verifiableMedicaidId?: string;
  verifiableMedicareId?: string;
  verifiableCommercialId?: string;
  organization: Organization;
}

/** Hidden until the user adds them via Columns. */
export const PAYER_VERIFIABLE_COLUMN_KEYS = [
  'verifiableMedicaidId',
  'verifiableMedicareId',
  'verifiableCommercialId',
] as const;

export const PAYER_TABLE_DEFAULT_COLUMN_KEYS = [
  'name',
  'state',
  'soharId',
  'stediNetworkId',
  'active',
];

export const PAYER_STATUS_FILTER_OPTIONS = [
  { value: 'Active', label: 'Active' },
  { value: 'Inactive', label: 'Inactive' },
];

/**
 * State options come from the loaded rows, so they reflect the same `_count: '500'`
 * page the table renders. A directory larger than 500 payers needs server-side paging
 * before either the rows or these options are complete.
 */
export function uniquePayerStateFilterOptions(
  payers: Array<{ state?: string }>,
): { value: string; label: string }[] {
  const states = [
    ...new Set(payers.map((p) => p.state).filter((s): s is string => Boolean(s))),
  ].sort();
  return states.map((s) => ({ value: s, label: s }));
}

export function updatePayerOrganization(
  organization: Organization,
  values: PayerFormInput,
): Organization {
  let updated: Organization = {
    ...organization,
    name: values.name.trim(),
    active: values.active,
  };
  updated = setPayerState(updated, values.state);
  updated = setSoharPayerId(updated, values.soharId);
  updated = setStediPayerNetworkId(updated, values.stediNetworkId);
  updated = setVerifiableMedicaidPayerId(updated, values.verifiableMedicaidId);
  updated = setVerifiableMedicarePayerId(updated, values.verifiableMedicareId);
  return setVerifiableCommercialPayerId(updated, values.verifiableCommercialId);
}

/** First visit: clear Organization search cache, then allow the search-owning child to mount. */
export function preparePayerDirectoryList(
  medplum: { invalidateSearches: InvalidateSearches },
  setListReady: (ready: boolean) => void,
): void {
  medplum.invalidateSearches('Organization');
  setListReady(true);
}

/**
 * After create/update: clear Organization search cache, then bump the child remount key.
 * `useSearchResources` does not refetch on invalidate alone — remounting the keyed child does.
 */
export function refreshPayerDirectoryList(
  medplum: { invalidateSearches: InvalidateSearches },
  bumpListKey: () => void,
): void {
  medplum.invalidateSearches('Organization');
  bumpListKey();
}

interface PayerDirectoryTableProps {
  canWrite: boolean;
  onRowClick: (payer: PayerRow) => void;
  onToggleActive: (payer: PayerRow, active: boolean) => void;
  visibleColumnOrder: string[] | null;
  onVisibleColumnOrderChange: (value: string[] | null) => void;
}

function PayerDirectoryTable({
  canWrite,
  onRowClick,
  onToggleActive,
  visibleColumnOrder,
  onVisibleColumnOrderChange,
}: PayerDirectoryTableProps): JSX.Element {
  const [columnModalOpen, { open: openColumnModal, close: closeColumnModal }] = useDisclosure(false);
  const [payerOrgs] = useSearchResources('Organization', {
    type: ORG_TYPE_PAYER,
    _count: '500',
    _sort: 'name',
  });

  const payers = useMemo(
    () =>
      (payerOrgs ?? [])
        .filter((org) => org.id && org.name)
        .map((org) => ({
          id: org.id as string,
          name: org.name as string,
          active: org.active !== false,
          state: getPayerState(org),
          soharId: getSoharPayerId(org),
          stediNetworkId: getStediPayerNetworkId(org),
          verifiableMedicaidId: getVerifiableMedicaidPayerId(org),
          verifiableMedicareId: getVerifiableMedicarePayerId(org),
          verifiableCommercialId: getVerifiableCommercialPayerId(org),
          organization: org,
        })),
    [payerOrgs],
  );

  const columns = useMemo((): UnifyTableColumn<PayerRow>[] => {
    return [
      { key: 'name', label: 'Name', width: 320 },
      { key: 'state', label: 'State', getValue: (p) => p.state ?? '', width: 88 },
      { key: 'soharId', label: 'Sohar ID', getValue: (p) => p.soharId ?? '' },
      { key: 'stediNetworkId', label: 'Stedi ID', getValue: (p) => p.stediNetworkId ?? '' },
      {
        key: 'verifiableMedicaidId',
        label: 'Verifiable Medicaid ID',
        getValue: (p) => p.verifiableMedicaidId ?? '',
      },
      {
        key: 'verifiableMedicareId',
        label: 'Verifiable Medicare ID',
        getValue: (p) => p.verifiableMedicareId ?? '',
      },
      {
        key: 'verifiableCommercialId',
        label: 'Verifiable Commercial ID',
        getValue: (p) => p.verifiableCommercialId ?? '',
      },
      {
        key: 'active',
        label: 'Active',
        pinRight: true,
        getValue: (p) => (p.active ? 'Active' : 'Inactive'),
        render: (p) => (
          <Group
            wrap="nowrap"
            onClick={(event) => event.stopPropagation()}
            onKeyDown={(event) => event.stopPropagation()}
          >
            <Switch
              color="gold"
              size="sm"
              checked={p.active}
              disabled={!canWrite}
              onChange={(event) => onToggleActive(p, event.currentTarget.checked)}
              aria-label={p.active ? `Deactivate ${p.name}` : `Activate ${p.name}`}
            />
          </Group>
        ),
      },
    ];
  }, [canWrite, onToggleActive]);

  const columnOptions = useMemo(
    () => columns.map((c) => ({ key: c.key, displayLabel: c.label })),
    [columns],
  );

  const visibleColumnKeys = visibleColumnOrder ?? PAYER_TABLE_DEFAULT_COLUMN_KEYS;
  const stateFilterOptions = useMemo(() => uniquePayerStateFilterOptions(payers), [payers]);

  return (
    <>
      <UnifyTable
        columns={columns}
        visibleColumnKeys={visibleColumnKeys}
        data={payers}
        getRowKey={(p) => p.id}
        onRowClick={onRowClick}
        searchable
        searchPlaceholder="Search payers…"
        sortable
        pagination
        defaultRowsPerPage={25}
        scrollMinWidth={720}
        filters={[
          {
            id: 'payer-status',
            placeholder: 'All statuses',
            columnKey: 'active',
            options: PAYER_STATUS_FILTER_OPTIONS,
            w: 160,
          },
          {
            id: 'payer-state',
            placeholder: 'All states',
            columnKey: 'state',
            options: stateFilterOptions,
            w: 140,
          },
        ]}
        extraFilters={
          <Button
            variant="outline"
            leftSection={<IconLayoutColumns size={16} />}
            onClick={openColumnModal}
          >
            Columns
          </Button>
        }
      />
      <ColumnSelectModal
        opened={columnModalOpen}
        onClose={closeColumnModal}
        allColumns={columnOptions}
        defaultColumnKeys={PAYER_TABLE_DEFAULT_COLUMN_KEYS}
        value={visibleColumnOrder}
        onApply={onVisibleColumnOrderChange}
      />
    </>
  );
}

export function PayersSettings(): JSX.Element {
  const medplum = useMedplum();
  const { role } = usePermissions();
  const canWrite = role === 'unify-admin';
  const [modalOpened, { open, close }] = useDisclosure(false);
  const [selectedPayer, setSelectedPayer] = useState<PayerRow>();
  const [modalKey, setModalKey] = useState(0);
  const [listReady, setListReady] = useState(false);
  const [listKey, setListKey] = useState(0);
  const [visibleColumnOrder, setVisibleColumnOrder] = useState<string[] | null>(null);

  useEffect(() => {
    preparePayerDirectoryList(medplum, setListReady);
  }, [medplum]);

  const openAddModal = useCallback(() => {
    setSelectedPayer(undefined);
    setModalKey((key) => key + 1);
    open();
  }, [open]);

  const openEditModal = useCallback(
    (payer: PayerRow) => {
      setSelectedPayer(payer);
      setModalKey((key) => key + 1);
      open();
    },
    [open],
  );

  const handleToggleActive = useCallback(
    async (payer: PayerRow, active: boolean) => {
      await medplum.updateResource({ ...payer.organization, active });
      refreshPayerDirectoryList(medplum, () => setListKey((key) => key + 1));
    },
    [medplum],
  );

  const handleSavePayer = useCallback(
    async (values: PayerFormInput) => {
      if (selectedPayer) {
        await medplum.updateResource(updatePayerOrganization(selectedPayer.organization, values));
      } else {
        await medplum.createResource(
          buildPayerOrganization({
            name: values.name,
            state: values.state,
            soharId: values.soharId,
            stediNetworkId: values.stediNetworkId,
            verifiableMedicaidId: values.verifiableMedicaidId,
            verifiableMedicareId: values.verifiableMedicareId,
            verifiableCommercialId: values.verifiableCommercialId,
            active: values.active,
          }),
        );
      }
      refreshPayerDirectoryList(medplum, () => setListKey((key) => key + 1));
    },
    [medplum, selectedPayer],
  );

  return (
    <Container size="lg" py="xl">
      <Stack gap="lg">
        <Group justify="space-between" align="flex-start" wrap="wrap" gap="md">
          <div>
            <Group gap="sm" align="center" wrap="wrap" mb="xs">
              <Title order={2} mb={0}>
                Payers
              </Title>
              {isUnifyAdminShellRole(role) ? null : <LocalhostOnlyBadge />}
            </Group>
            <Text size="sm" c="dimmed">
              Configure insurance payers and reimbursement preferences
            </Text>
          </div>
          {canWrite && (
            <Button leftSection={<IconPlus size={16} />} onClick={openAddModal}>
              Add Payer
            </Button>
          )}
        </Group>

        {listReady && (
          <PayerDirectoryTable
            key={listKey}
            canWrite={canWrite}
            onRowClick={canWrite ? openEditModal : () => undefined}
            onToggleActive={handleToggleActive}
            visibleColumnOrder={visibleColumnOrder}
            onVisibleColumnOrderChange={setVisibleColumnOrder}
          />
        )}
      </Stack>

      {canWrite && (
      <AddPayerModal
        key={modalKey}
        opened={modalOpened}
        onClose={close}
        onSubmit={handleSavePayer}
        initialValues={
          selectedPayer
            ? {
                name: selectedPayer.name,
                active: selectedPayer.active,
                state: selectedPayer.state ?? '',
                soharId: selectedPayer.soharId ?? '',
                stediNetworkId: selectedPayer.stediNetworkId ?? '',
                verifiableMedicaidId: selectedPayer.verifiableMedicaidId ?? '',
                verifiableMedicareId: selectedPayer.verifiableMedicareId ?? '',
                verifiableCommercialId: selectedPayer.verifiableCommercialId ?? '',
              }
            : undefined
        }
        editing={Boolean(selectedPayer)}
      />
      )}
    </Container>
  );
}
